import React, { useEffect, useId, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';

// AUTO-WA-39: plain-language answers for WhatsApp jargon, next to where the
// word appears. One glossary, so every page explains a term the same way.
export const GLOSSARY = {
  window: {
    title: 'The 24-hour reply window',
    text: 'After a customer messages you, you can reply with any message for 24 hours. After that, WhatsApp only lets businesses send an approved template until the customer writes again. From October 2026 the first 1,000 replies a month per number are free; Meta charges for the rest.',
  },
  template: {
    title: 'Templates',
    text: 'Messages you write once and Meta approves. You need one to message people first, for broadcasts and after the 24-hour window. Approval usually takes minutes.',
  },
  category: {
    title: 'Template categories',
    text: 'Marketing is for offers and news. Utility is for updates people expect, like orders or appointments, and costs less. Authentication is only for one-time codes.',
  },
  tags: {
    title: 'Tags',
    text: 'Labels you put on contacts, like VIP or Wholesale. Use them to find people quickly and to choose who gets a broadcast.',
  },
  quality: {
    title: 'Quality rating',
    text: 'How customers react to your messages. Green is good. If many people block or report you it turns yellow or red, and Meta may lower your daily limit.',
  },
  tier: {
    title: 'Daily messaging limit',
    text: 'How many different people your business can message outside the 24-hour window in any 24 hours, shared by all your numbers. Replies to customers who wrote first do not count. It grows from 250 to 2,000, 10,000, 1,00,000 and unlimited as you send good-quality messages.',
  },
  bot: {
    title: 'Bots',
    text: 'Bots reply to customers for you, day and night. An AI chat understands questions in your customers’ own words; a guided chat follows buttons and menus you set up.',
  },
  quickReplies: {
    title: 'Quick replies',
    text: 'Answers your team sends often, like store timings or prices. In a chat, press the lightning button and pick one; it is added to your message for you to check and send.',
  },
  broadcast: {
    title: 'Broadcasts',
    text: 'One template sent to many people at once. Each person gets it as a normal WhatsApp message, and their replies come to Chats.',
  },
};

export default function HelpTip({ term, label, align = 'left' }) {
  const entry = GLOSSARY[term];
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const id = useId();
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);
  if (!entry) return null;
  return (
    <span ref={rootRef} className="wa-help-tip">
      <button type="button" className="wa-help-tip-btn" aria-expanded={open} aria-controls={id} aria-label={label || `What is ${entry.title.toLowerCase()}?`} onClick={() => setOpen((value) => !value)}>
        <HelpCircle size={14} aria-hidden="true" />
      </button>
      {open && (
        <span id={id} role="tooltip" className={`wa-help-tip-card${align === 'right' ? ' is-right' : ''}`}>
          <strong>{entry.title}</strong>
          {entry.text}
        </span>
      )}
    </span>
  );
}
